import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import Header from "@/components/Header";
import BottomBar from "@/components/BottomBar";
import kyc01 from "@/assets/images/help/kyc-01.png";
import kyc02 from "@/assets/images/help/kyc-02.png";
import kyc03 from "@/assets/images/help/kyc-03.png";
import kyc04 from "@/assets/images/help/kyc-04.png";
import kyc05 from "@/assets/images/help/kyc-05.png";
import kyc06 from "@/assets/images/help/kyc-06.png";
import kyc07 from "@/assets/images/help/kyc-07.png";
import kyc08 from "@/assets/images/help/kyc-08.png";
import kyc09 from "@/assets/images/help/kyc-09.png";
import kyc10 from "@/assets/images/help/kyc-10.png";
import kyc11 from "@/assets/images/help/kyc-11.png";
import kyc12 from "@/assets/images/help/kyc-12.png";

export default function HelpKYC() {
  const navigate = useNavigate();

  const steps = [
    {
      title: "Step 1: Start POA Submission",
      description:
        "Click the \"Submit POA Document\" button to begin submitting your Proof of Address (POA).",
      image: kyc01,
    },
    {
      title: "Step 2: Verify POA",
      description:
        "Take a photo of your POA (English version of your resident registration) or upload it for verification.",
      image: kyc02,
    },
    {
      title: "Step 3: Start KYC",
      description:
        "Click the \"Open KYC Verification\" button to begin the KYC process.",
      image: kyc03,
    },
    {
      title: "Step 4: Select Country and ID Type",
      description:
        "Choose the country for verification and the type of identification document you will upload.",
      image: kyc04,
    },
    {
      title: "Step 5: Go to the KYC Site",
      description:
        "Scan the QR code on the screen with your mobile phone.\n(Alternatively, you can proceed on your PC via SMS or by copying the link.)\n\nIf you are accessing from a mobile device, the KYC site may open automatically without scanning the QR code.",
      image: kyc05,
    },
    {
      title: "Step 6: KYC Site Opens",
      description:
        "Click the \"Continue\" button and follow the on-screen instructions to proceed with the KYC process.",
      image: kyc06,
    },
    {
      title: "Step 7: Verify Your ID",
      description:
        "Take a photo of your identification document or upload it.\n\nImportant Notes:\n · Make sure the entire ID is visible and not cropped.\n · Ensure all text and details on the ID are clear and legible.\n · Avoid glare, shadows, or blurriness when capturing the image.",
      image: kyc07,
    },
    {
      title: "Step 8: Verify Your Face",
      description:
        "Take a photo of your face or upload it.\n\nImportant Notes:\n · Make sure your face fills the frame.\n · Ensure your entire face is clearly visible, including eyes, nose, and mouth.\n · Avoid blurriness, glare, or shadows.\n · Keep the background simple and uncluttered",
      image: kyc08,
    },
    {
      title: "Step 9: Complete KYC Upload",
      description:
        "Your ID and face photo have been successfully uploaded.",
      image: kyc09,
    },
    {
      title: "Step 10: KYC Review",
      description:
        "The information you submitted is under review.\nYou can check whether your KYC has been verified by clicking the \"Start KYC\" button.",
      image: kyc10,
    },
    {
      title: "Step 11: Check KYC Status",
      description:
        "Click the \"Try Again\" button to see if your KYC has been completed.",
      image: kyc11,
    },
    {
      title: "Step 12: KYC Completed",
      description:
        "You can check on the home screen that your KYC has been verified.\nThe KYC process has been successfully completed.\n\nProceed to the next step.",
      image: kyc12,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header showBack={true} onBackClick={() => navigate("/help")} />

      <div className="max-w-md mx-auto px-4 pb-20">
        {/* Title */}
        <div className="mt-6 mb-6">
          <h1 className="text-2xl font-bold mb-2">
            User Verification Step 3: KYC
          </h1>
        </div>

        {/* Introduction */}
        <div className="space-y-4 mb-8">
          <p className="text-sm font-semibold whitespace-pre-line">
            KYC verification is required to complete user verification.
            {"\n\n"}
            Please prepare the following items for the KYC process.
            {"\n\n"}
            Required Documents:
            {"\n"} · POA (Proof of Address): English version of your resident
            registration
            {"\n"} · Identification: Passport or English driver's license
            {"\n"} · Face Photo
          </p>
          <div className="rounded-lg overflow-hidden shadow-md">
            <img src={kyc10} alt="KYC Process" className="w-full h-auto" />
          </div>
          <p className="text-sm font-semibold whitespace-pre-line">
            Once you are ready, click the "Start KYC" button on the home screen
            to begin the KYC process.
            {"\n\n"}※ Please use your passport first. If you do not have a
            passport, you may use an English driver's license instead.
          </p>
        </div>

        {/* Steps */}
        <div className="space-y-8">
          {steps.map((step, index) => (
            <div key={index} className="space-y-4">
              <p className="text-sm font-semibold whitespace-pre-line">
                {step.title}
              </p>
              <div className="rounded-lg overflow-hidden shadow-md">
                <img
                  src={step.image}
                  alt={`Step ${index + 1}`}
                  className="w-full h-auto"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Next Step */}
        <div className="mt-8">
          <Button
            onClick={() => navigate("/help/6")}
            className="w-full h-12 flex items-center justify-between"
          >
            <span>Next: Wallet Creation</span>
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <BottomBar />
    </div>
  );
}
